import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import {
  ConnectionChangeRequestTypeDisplayValues,
  ConnectionChangeRequestTypeEnum
} from '@bo-client/shared/models/connectionChange.dto';
import {
  AllAreaEnum,
  AreaDisplayName
} from '@bo-client/activation-management/shared/models/commissioningModel';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { path } from '@bo-client/shared/constants/path';
import { Observable, of } from 'rxjs';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { combineLatestWith, map, shareReplay, startWith } from 'rxjs/operators';

@Component({
  selector: 'app-order-request-wizard-entry',
  templateUrl: './order-request-wizard-entry.component.html',
  styleUrl: './order-request-wizard-entry.component.scss'
})
export class OrderRequestWizardEntryComponent implements OnInit {
  orderRequestForm: FormGroup<{
    area: FormControl<AllAreaEnum>;
    type: FormControl<ConnectionChangeRequestTypeEnum>;
  }>;

  options$: Observable<AllowedRequestTypes> = of({});
  currentRequestOptions$: Observable<ConnectionChangeRequestTypeEnum[]> = of([]);

  private destroyRef = inject(DestroyRef);
  private formBuilder = inject(FormBuilder);
  private router = inject(Router);
  private tenantConfig$ = inject(DataShareService).tenantConfig$;

  constructor() {}
  ngOnInit() {
    this.orderRequestForm = this.formBuilder.group({
      area: [null as AllAreaEnum, Validators.required],
      type: [null as ConnectionChangeRequestTypeEnum, Validators.required]
    });
    this.orderRequestForm.controls.area.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.orderRequestForm.controls.type.setValue(null);
      });
    this.options$ = this.tenantConfig$.pipe(
      takeUntilDestroyed(this.destroyRef),
      map((config) => {
        if (!config?.connectionChangeRequest?.connectionChangeRequestTypes?.length) {
          return {} as AllowedRequestTypes;
        }
        const options: AllowedRequestTypes = {};
        config.connectionChangeRequest.connectionChangeRequestTypes.forEach((setting) => {
          if (!setting || !setting.isActive) {
            return;
          }
          options[setting.area] = [...(options[setting.area] || []), setting.connectionChangeRequestType];
        });
        return options;
      }),
      shareReplay(1)
    );
    this.currentRequestOptions$ = this.orderRequestForm.controls.area.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      startWith(this.orderRequestForm.controls.area.value),
      combineLatestWith(this.options$),
      map(([selectedArea, options]) => {
        return options[selectedArea] || [];
      }),
      shareReplay(1)
    );
  }

  onSubmit() {
    if (
      this.orderRequestForm.invalid ||
      !this.orderRequestForm.value.type ||
      !this.orderRequestForm.value.area
    ) {
      this.orderRequestForm.markAsTouched();
      return;
    }
    const { area, type } = this.orderRequestForm.value;
    this.router
      .navigate([`${path.external}/${path.orderRequestWizard}/${area.toLowerCase()}/${type.toLowerCase()}`])
      .then();
  }

  getAreaOptions(types: AllowedRequestTypes) {
    if (!types) return [];
    return Object.keys(types);
  }

  readonly AreaDisplayName = AreaDisplayName;
  readonly ConnectionChangeRequestTypeDisplayValues = ConnectionChangeRequestTypeDisplayValues;
}

type AllowedRequestTypes = Partial<{
  [A in AllAreaEnum]: ConnectionChangeRequestTypeEnum[];
}>;
